import { Alert, Box } from "@mui/material";
import Loader from "components/Loader";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  OrganizationUserDetails,
  SaveUserRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import UserForm from "./UserForm";

const UserUpdateForm = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const {
    usersListUrl,
    userDetailsUrl,
    organizationUserService,
    userService,
  } = useGlobalContext();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: user, isLoading, isError } = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", id],
    () => organizationUserService.getOrganizationUserDetails(id!),
    {
      enabled: !!id,
    }
  );

  if (!id) return <Navigate to={usersListUrl()} replace />;
  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="fetchError" />
        </Alert>
      </Box>
    );
  }

  const onSubmit = async (values: OrganizationUserDetails) => {
    const updatedUser = new SaveUserRequest({
      id: values.id,
      organizationId: values.organizationId,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      timeZone: values.timeZone,
      externalSourceId: values.externalSourceId,
      roleNames: values.roles,
    });
    try {
      const response = await registerPromise(userService.saveUser(updatedUser));
      queryClient.invalidateQueries(["getOrganizationUserDetails", id]);
      queryClient.invalidateQueries("searchOrganizationUsers");
      navigate(userDetailsUrl(response?.id));
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <UserForm
      user={user}
      onSubmit={onSubmit}
      submitLabel={t({ id: "userFormUpdateButton" })}
      onCancel={() => navigate(userDetailsUrl(id))}
    />
  );
};

export default UserUpdateForm;
